import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BackgroundGradientAnimation } from '@/components/ui/background-gradient-animation';
import { LOCAL_STORAGE_KEYS } from '@/utils/storageKeys';
import { lockBodyScroll, unlockBodyScroll } from '@/utils/scrollLock';

interface AdModalProps {
  isDarkMode: boolean;
}

// Check if ad should be shown based on message count
export const shouldShowAd = (messageCount: number): boolean => {
  // Show after 2nd message (one time), then after 7th, then every 10th message
  if (messageCount === 2) return true;
  if (messageCount === 7) return true;
  if (messageCount > 7 && (messageCount - 7) % 10 === 0) return true;
  return false;
};

const LegacyAdModalCard = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className="relative w-full max-w-[360px] rounded-2xl border border-transparent overflow-hidden [background:linear-gradient(45deg,#0b1220,theme(colors.slate.900)_50%,#0b1220)_padding-box,conic-gradient(from_var(--border-angle),theme(colors.slate.600/.5)_76%,theme(colors.cyan.400)_82%,theme(colors.cyan.300)_86%,theme(colors.indigo.400)_90%,theme(colors.cyan.500)_94%,theme(colors.slate.600/.5))_border-box] animate-border shadow-2xl">
      <div
        className="rounded-[14px] p-4 flex flex-col gap-4 overflow-y-auto max-h-[80vh]"
        style={{
          backgroundColor: 'hsla(240, 15%, 9%, 1)',
          backgroundImage:
            'radial-gradient(at 88% 40%, hsla(240, 15%, 9%, 1) 0px, transparent 85%),\nradial-gradient(at 49% 30%, hsla(240, 15%, 9%, 1) 0px, transparent 85%),\nradial-gradient(at 14% 26%, hsla(240, 15%, 9%, 1) 0px, transparent 85%),\nradial-gradient(at 0% 64%, hsl(189, 99%, 26%) 0px, transparent 85%),\nradial-gradient(at 41% 94%, hsl(189, 97%, 36%) 0px, transparent 85%),\nradial-gradient(at 100% 99%, hsl(188, 94%, 13%) 0px, transparent 85%)',
          boxShadow: 'inset 0px -16px 24px 0px rgba(255, 255, 255, 0.25)'
        }}
      >
        <div className="card_title__container">
          <span className="text-white text-base">Premium Experience</span>
          <p className="mt-1 w-2/3 text-[10px] text-neutral-300">Discover amazing features</p>
        </div>

        <hr className="w-full h-[2px] border-0 bg-neutral-800" />

        <ul className="flex flex-col gap-2">
          <li className="flex items-center gap-2">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-cyan-400">
              <svg className="w-3 h-3 fill-black" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
              </svg>
            </span>
            <span className="text-white text-sm">Enhanced AI Features</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-cyan-400">
              <svg className="w-3 h-3 fill-black" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
              </svg>
            </span>
            <span className="text-white text-sm">Priority Processing</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-cyan-400">
              <svg className="w-3 h-3 fill-black" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
              </svg>
            </span>
            <span className="text-white text-sm">No Daily Limits</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-cyan-400">
              <svg className="w-3 h-3 fill-black" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
              </svg>
            </span>
            <span className="text-white text-sm">Priority Support</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-cyan-400">
              <svg className="w-3 h-3 fill-black" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
              </svg>
            </span>
            <span className="text-white text-sm">Early Access to New Models</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-cyan-400">
              <svg className="w-3 h-3 fill-black" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" />
              </svg>
            </span>
            <span className="text-white text-sm">Advanced Image Generation</span>
          </li>
        </ul>

        <div className="space-y-3 pt-1">
          <Button
            onClick={() => {
              window.open('https://oreog.netlify.app/join', '_blank');
              onClose();
            }}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 rounded-full transition-all duration-200"
          >
            Upgrade Now
          </Button>
          <Button
            onClick={onClose}
            variant="ghost"
            className="w-full text-slate-300 hover:bg-transparent hover:text-slate-200 py-2 text-sm"
          >
            Continue with Free
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdModalCard = ({ isDarkMode, onClose }: { isDarkMode: boolean; onClose: () => void }) => {
  const navigate = useNavigate();
  const baseSurface = isDarkMode
    ? 'bg-[#202123] text-slate-100 border-[#3f3f46] shadow-[0_22px_60px_rgba(0,0,0,0.7)]'
    : 'bg-white text-slate-900 border-[#ececec] shadow-[0_18px_45px_rgba(15,23,42,0.22)]';
  const bodyText = isDarkMode ? 'text-slate-200' : 'text-slate-600';
  const mutedText = isDarkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div
      className={`w-full max-w-lg rounded-[24px] overflow-hidden border ${baseSurface}`}
    >
      <div className="h-40 sm:h-44 w-full">
        <BackgroundGradientAnimation
          containerClassName="h-full"
          className="absolute inset-0 z-50 flex items-center justify-center text-center text-white text-2xl sm:text-3xl font-semibold tracking-tight"
          interactive={true}
        >
          <span>Try Oreog Pro</span>
        </BackgroundGradientAnimation>
      </div>

      <div className="px-6 pt-6 pb-5 sm:px-8 sm:pt-7 sm:pb-6 space-y-5">
        <p className={`text-sm sm:text-[15px] leading-relaxed ${bodyText}`}>
          Unlock a smoother AI experience in Oreog: faster responses, higher daily limits, and priority access to new features.
        </p>

        <p className={`text-xs sm:text-[13px] leading-relaxed ${mutedText}`}>
          Continue using the free plan anytime. No commitment required.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 pt-1 sm:pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className={`w-full rounded-full border px-4 py-2 text-sm font-medium ${isDarkMode ? 'border-[#3f3f46] bg-transparent text-slate-200 hover:bg-[#3f3f46]' : 'border-[#D9D9D9] bg-white text-slate-800 hover:bg-slate-100'}`}
          >
            Maybe later
          </Button>
          <Button
            onClick={() => {
              navigate('/upgrade');
              onClose();
            }}
            className={`w-full rounded-full text-sm font-semibold px-4 py-2 ${isDarkMode ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-black'}`}
          >
            Upgrade now
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AdModal = ({ isDarkMode }: AdModalProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkAndShowAd = () => {
      const messageRaw =
        localStorage.getItem(LOCAL_STORAGE_KEYS.totalMessageCount) ||
        localStorage.getItem('totalMessageCount') ||
        '0';
      const lastAdRaw =
        localStorage.getItem(LOCAL_STORAGE_KEYS.lastAdMessageCount) ||
        localStorage.getItem('lastAdMessageCount') ||
        '0';

      const messageCount = Number.parseInt(messageRaw, 10) || 0;
      const lastAdAt = Number.parseInt(lastAdRaw, 10) || 0;
      
      if (shouldShowAd(messageCount) && messageCount > lastAdAt) {
        setIsVisible(true);
        localStorage.setItem(LOCAL_STORAGE_KEYS.lastAdMessageCount, messageCount.toString());
      }
    };

    checkAndShowAd();
  }, []);

  useEffect(() => {
    if (isVisible) {
      lockBodyScroll();
      return () => {
        unlockBodyScroll();
      };
    }
  }, [isVisible]);

  if (!isVisible) return null;

  const onClose = () => {
    setIsVisible(false);
    unlockBodyScroll();
  };

  return (
    <div className="fixed inset-0 dark:bg-black/00 bg-white/00 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="relative w-full max-w-sm sm:max-w-md mx-auto max-h-[90vh]">
        <div className="flex items-center justify-center">
          <AdModalCard isDarkMode={isDarkMode} onClose={onClose} />
        </div>
      </div>
    </div>
  );
};
